import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import type { Path } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useEstateOptions } from '@/api/estate-options';
import { FieldValid } from '@/components/form/field-valid';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { getEstateOptions } from '@/features/estates-property/api/estate';
import { FloorOptions } from '@/features/estates-property/utils/floorOptions';

import { useUnlockEstateAnalysis } from '../api/analytics';
import type { EstateValidationSchemaType } from '../schemas/estate-validation';
import { getEstateValidationSchema, transformEstate } from '../schemas/estate-validation';
import { AnalyticsRequestForm } from './analytics-request-form';
import { canWrite } from '@/features/permissions/resource-permissions';

const route = getRouteApi(
  '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout/analytics'
);

export const AnalysisValidation = () => {
  const { t } = useTranslation();
  const estate = route.useLoaderData();
  const { data: estateOptions } = useEstateOptions();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [isEditing, setIsEditing] = useState(false);
  const [savedEstateData, setSavedEstateData] = useState<{
    data: EstateValidationSchemaType;
    estateId: string;
  } | null>(null);

  const estateSchema = getEstateValidationSchema();

  const transformedEstateData = useMemo(() => transformEstate(estate), [estate]);
  const estateData =
    savedEstateData?.estateId === estate.id ? savedEstateData.data : transformedEstateData;

  const canWriteGeneral = canWrite(estate.metadata?.permissions, 'general');

  const { mutateAsync: unlockEstate } = useUnlockEstateAnalysis({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('requestAnalyticsSuccess'));
        queryClient
          .invalidateQueries({ queryKey: getEstateOptions(estate.id).queryKey })
          .then(() => {
            router.invalidate();
          });
      },
      onError: (error) => {
        toast.error(error.message);
      },
    },
  });

  const handleRequestAnalytics = async () => {
    if (!canWriteGeneral) {
      return;
    }

    await unlockEstate(estate.id);
  };

  const methods = useForm<EstateValidationSchemaType>({
    resolver: zodResolver(estateSchema),
    defaultValues: estateData as EstateValidationSchemaType,
  });

  const { reset, watch } = methods;

  useEffect(() => {
    // @ts-expect-error
    reset((prevValues) => ({
      ...prevValues,
      ...estateData,
    }));
  }, [reset, estateData]);

  const propertyType = watch('estateDetails.propertyType');

  const result = useMemo(() => estateSchema.safeParse(estateData), [estateData, estateSchema]);

  if (!result.success || isEditing) {
    if (!canWriteGeneral) {
      return (
        <Card className="rounded-md p-6 text-left">
          <h3 className="text-[16px] font-bold">{t('analyticsHasNotBeenRequestedYet')}</h3>
        </Card>
      );
    }

    return (
      <AnalyticsRequestForm
        estateData={estateData}
        onSaved={(data) => setSavedEstateData({ data, estateId: estate.id })}
        setIsEditing={setIsEditing}
      />
    );
  }

  return (
    <Card className="rounded-md p-6 text-left">
      <h3 className="text-[16px] font-bold">{t('propertyInformation')}</h3>
      <p className="text-sm text-text-secondary">{t('allRequiredInformationProvided')}</p>
      <FormProvider {...methods}>
        <form className="mt-6 space-y-6" onSubmit={methods.handleSubmit(handleRequestAnalytics)}>
          <div className="grid grid-cols-2">
            <div className="space-y-4">
              <FieldValid
                methods={methods}
                name="estateDetails.marketType"
                label={t('marketing-type')}
                options={estateOptions?.marketingType}
              />
              <FieldValid methods={methods} name="address.address" label={t('address')} />
              <FieldValid
                methods={methods}
                name="estateDetails.propertyType"
                label={t('property-type')}
                options={estateOptions?.propertyType}
              />
            </div>
            <div className="space-y-4">
              <FieldValid
                methods={methods}
                name="estateDetails.buildingYear"
                label={t('buildingYear')}
              />
              <FieldValid
                methods={methods}
                name="estateAreasRooms.livingArea"
                label={t('livingArea')}
                type="square"
              />
              {propertyType === 'house' ? (
                <FieldValid
                  methods={methods}
                  name={'estateAreasRooms.plotArea' as Path<EstateValidationSchemaType>}
                  label={t('plotArea')}
                  type="square"
                />
              ) : null}
              <FieldValid
                methods={methods}
                name="estateAreasRooms.totalNumberOfRooms"
                label={t('totalNumberOfRooms')}
              />
              <FieldValid
                methods={methods}
                name="estateAreasRooms.numberOfBathrooms"
                label={t('numberOfBathrooms')}
              />
              <FieldValid
                methods={methods}
                name="estateAreasRooms.balconyArea"
                label={t('balconyArea')}
                type="square"
              />
              <FieldValid
                methods={methods}
                name="estateAreasRooms.gardenArea"
                label={t('gardenArea')}
                type="square"
              />
              {propertyType === 'apartment' ? (
                <>
                  <FieldValid
                    methods={methods}
                    name="estateAreasRooms.floor"
                    label={t('floor')}
                    options={FloorOptions}
                  />
                  <FieldValid
                    methods={methods}
                    name="estateAreasRooms.hasElevator"
                    label={t('hasElevator')}
                    options={[
                      {
                        id: '1',
                        title: t('yes'),
                      },
                      {
                        id: '0',
                        title: t('no'),
                      },
                    ]}
                  />
                </>
              ) : null}
              <FieldValid
                methods={methods}
                name="estateEnergyDetails.hwbClass"
                label={t('hwbClass')}
                options={estateOptions?.energyClasses}
              />
            </div>
          </div>
          <div className="rounded border border-caution bg-caution/5 p-6 text-base font-normal text-caution">
            <span className="font-semibold">{t('disclaimer')}:</span> {t('analyticDisclaimer')}
          </div>
          {canWriteGeneral ? (
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                size="md"
                className="!px-12"
                onClick={() => setIsEditing(true)}
              >
                {t('back')}
              </Button>
              <Button type="submit" variant="contained" size="md" className="!px-12">
                {t('requestAnalytics')}
              </Button>
            </div>
          ) : null}
        </form>
      </FormProvider>
    </Card>
  );
};
